import { Route, Router, type RouteSectionProps } from '@solidjs/router';
import { createResource, lazy, Show } from 'solid-js';
import { appConfig, applyRemoteConfigs } from '@/config/env';
import { Loader } from '@/modules/dom/Loader';
import { RouteCrossfade } from '@/modules/dom/RouteCrossfade';
import { useDocumentTitle } from '@/modules/dom/useDocumentTitle';
import { createLogger } from '@/modules/logger';
import { remoteConfigName, resolveRemoteConfigs } from '@/modules/config';
import { RuntimeProvider } from '@/modules/runtime';
import '@/modules/layouts/preload';

const FunnelPage = lazy(() => import('./pages/FunnelPage'));
const LayoutPatchSpikePage = lazy(() => import('./pages/LayoutPatchSpikePage'));
const DOfferPage = lazy(() => import('./pages/DOfferPage'));
const PaywallPage = lazy(() => import('./pages/PaywallPage'));
const PostPaymentRouter = lazy(() => import('./pages/postpayment/PostPaymentRouter'));

/** Remote config resolves before mount: layout variant, store key and user slug depend on it. */
const bootstrapConfigs = async () => {
  const logger = createLogger(appConfig);
  try {
    await applyRemote(logger);
  } catch (error) {
    // Remote config must not break the quiz: keep the query/env variant.
    logger.logError(error, 'remoteConfigBootstrapFailed');
  }
  return true;
};

const applyRemote = async (logger: ReturnType<typeof createLogger>) => {
  const hasRemote = Boolean(remoteConfigName(appConfig.search));
  const resolved = await resolveRemoteConfigs({
    search: appConfig.search,
    logger,
    amplitude: hasRemote
      ? async () => {
          const { AmplitudeConfigProvider } = await import('@/modules/config/amplitude');
          return new AmplitudeConfigProvider(appConfig, logger);
        }
      : undefined,
    firebase: hasRemote
      ? async () => {
          const { FirebaseConfigProvider } = await import('@/modules/config/firebase');
          return new FirebaseConfigProvider(appConfig, logger);
        }
      : undefined,
  });
  const before = appConfig.onboardingVariant;
  applyRemoteConfigs(appConfig, resolved);
  if (appConfig.onboardingVariant !== before && window.__organicLayoutPreload) {
    // index.html already preloads the query variant layout: swallow it to avoid unhandled rejection.
    window.__organicLayoutPreload.catch(() => undefined);
  }
  if (hasRemote) {
    logger.logInfo(
      { variant: appConfig.onboardingVariant, variantSource: appConfig.query.variantSource },
      'remoteConfigsApplied',
    );
  }
};

/** Router root: the tab title follows the route, the crossfade keeps the leaving screen underneath. */
const FunnelRoot = (props: RouteSectionProps) => {
  useDocumentTitle();
  return <RouteCrossfade {...props} />;
};

export default function App() {
  const [ready] = createResource(bootstrapConfigs);
  return (
    <Show when={ready()} fallback={<Loader />}>
      {/* Built after remote config: the runtime reads the resolved language and variant once. */}
      <RuntimeProvider>
        <Router root={FunnelRoot} base={appConfig.basePath}>
          <Route path="/paywall" component={PaywallPage} />
          <Route path="/d-offer" component={DOfferPage} />
          <Route path="/success" component={PostPaymentRouter} />
          <Route path="/register" component={PostPaymentRouter} />
          <Route path="/upsells" component={PostPaymentRouter} />
          <Route path="/download" component={PostPaymentRouter} />
          <Route path="/layout-spike" component={LayoutPatchSpikePage} />
          <Route path="/*" component={FunnelPage} />
        </Router>
      </RuntimeProvider>
    </Show>
  );
}
